import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
  Typography,
  Skeleton,
  Menu,
  MenuItem,
  Badge,
} from '@mui/material';
import { useDragDrop } from './DragDropProvider';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreateNewFolder as CreateNewFolderIcon,
  DriveFileMove as MoveIcon,
  CloudUpload as UploadIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';

const FolderTreeItem = ({ 
  folder, 
  depth = 0, 
  isSelected, 
  onFolderClick, 
  onFolderEdit, 
  onFolderDelete,
  onFolderUpload,
  onFolderZipDownload,
  canManage,
  onDropDocument 
}) => {
  const [expanded, setExpanded] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const { draggedItem, draggedType, draggedFiles, startDrag, startFileDrag, endDrag } = useDragDrop();
  
  const handleToggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };
  
  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };
  
  const handleEdit = () => {
    handleMenuClose();
    onFolderEdit(folder);
  };
  
  const handleDelete = () => {
    handleMenuClose();
    onFolderDelete(folder.id);
  };
  
  const handleUpload = () => {
    handleMenuClose();
    onFolderUpload(folder);
  };
  
  const handleZipDownload = () => {
    handleMenuClose();
    if (onFolderZipDownload) {
      onFolderZipDownload(folder);
    }
  };
  
  const hasSubFolders = folder.subFolders && folder.subFolders.length > 0;
  
  // Handle drag events for documents and files
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if dragging documents or files from desktop
    if (draggedType === 'document') {
      e.dataTransfer.dropEffect = 'copy';
      setDragOver(true);
    } else if (e.dataTransfer.types.includes('Files')) {
      // Files from desktop/file system
      e.dataTransfer.dropEffect = 'copy';
      setDragOver(true);
    }
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    if (draggedType === 'document' && draggedItem) {
      // Handle document drag from within the app
      onDropDocument(folder.id, draggedItem);
      endDrag();
    } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Handle file drag from desktop
      const files = Array.from(e.dataTransfer.files);
      console.log('Files dropped on folder:', files);
      
      // Trigger the upload modal with these files
      onFolderUpload(folder, files);
      endDrag();
    }
  };
  
  // Handle dragging folders
  const handleFolderDragStart = (e) => {
    e.stopPropagation();
    startDrag(folder, 'folder');
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleFolderDragEnd = (e) => {
    e.stopPropagation();
    endDrag();
  };
  
  return (
    <>
      <ListItem
        disablePadding
        sx={{ 
          pl: depth * 2,
          backgroundColor: dragOver ? 'action.hover' : 'transparent',
          transition: 'background-color 0.2s',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        secondaryAction={
          canManage && (
            <>
              <IconButton 
                edge="end" 
                size="small"
                onClick={handleMenuOpen}
                sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleUpload}>
                  <UploadIcon fontSize="small" sx={{ mr: 1 }} />
                  Upload File
                </MenuItem>
                <MenuItem onClick={handleZipDownload}>
                  <ArchiveIcon fontSize="small" sx={{ mr: 1 }} />
                  Zip & Download
                </MenuItem>
                <MenuItem onClick={handleEdit}>
                  <EditIcon fontSize="small" sx={{ mr: 1 }} />
                  Edit
                </MenuItem>
                <MenuItem onClick={handleDelete}>
                  <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                  Delete
                </MenuItem>
              </Menu>
            </>
          )
        }
      >
        <ListItemButton
          selected={isSelected}
          onClick={() => onFolderClick(folder.id, folder.name)}
          draggable={canManage}
          onDragStart={handleFolderDragStart}
          onDragEnd={handleFolderDragEnd}
          sx={{ 
            borderRadius: 1,
            cursor: canManage ? 'grab' : 'pointer',
            '&:active': {
              cursor: canManage ? 'grabbing' : 'pointer',
            }
          }}
        >
          {hasSubFolders && (
            <IconButton
              size="small"
              onClick={handleToggleExpand}
              sx={{ mr: 0.5, p: 0.5 }}
            >
              {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          )}
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Badge badgeContent={folder.itemCount} color="primary" max={99}>
              {expanded ? <FolderOpenIcon /> : <FolderIcon />}
            </Badge>
          </ListItemIcon>
          <ListItemText 
            primary={folder.name}
            secondary={folder.description}
            primaryTypographyProps={{ 
              variant: 'body2',
              noWrap: true,
            }}
            secondaryTypographyProps={{
              variant: 'caption',
              noWrap: true,
            }}
          />
        </ListItemButton>
      </ListItem>
      
      {hasSubFolders && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {folder.subFolders.map(subFolder => (
              <FolderTreeItem
                key={subFolder.id}
                folder={subFolder}
                depth={depth + 1}
                isSelected={isSelected}
                onFolderClick={onFolderClick}
                onFolderEdit={onFolderEdit}
                onFolderDelete={onFolderDelete}
                onFolderUpload={onFolderUpload}
                onFolderZipDownload={onFolderZipDownload}
                canManage={canManage}
                onDropDocument={onDropDocument}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const FolderTree = ({ 
  folders, 
  currentFolderID, 
  onFolderClick, 
  onFolderEdit, 
  onFolderDelete,
  onFolderUpload,
  onFolderZipDownload,
  canManage,
  loading,
  onDropDocument 
}) => {
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }
  
  if (!folders || folders.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <FolderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No folders yet
        </Typography>
        {canManage && (
          <Typography variant="caption" color="text.secondary">
            Click "New Folder" to create your first folder
          </Typography>
        )}
      </Box>
    );
  }
  
  return (
    <List sx={{ width: '100%' }}>
      {folders.map(folder => (
        <FolderTreeItem
          key={folder.id}
          folder={folder}
          depth={0}
          isSelected={currentFolderID === folder.id}
          onFolderClick={onFolderClick}
          onFolderEdit={onFolderEdit}
          onFolderDelete={onFolderDelete}
          onFolderUpload={onFolderUpload}
          onFolderZipDownload={onFolderZipDownload}
          canManage={canManage}
          onDropDocument={onDropDocument}
        />
      ))}
    </List>
  );
};

export default FolderTree;